import { useState, useMemo, useCallback } from 'react';
import { calculatePagination, createSortFunction, createSortHandler } from '../utils/tableUtils';

/**
 * Custom hook for table pagination and sorting
 * 
 * @param {Array} items - Array of items to paginate and sort
 * @param {Object} options - Configuration options
 * @param {number} options.itemsPerPage - Number of items per page (default: 10)
 * @param {string} options.defaultSortField - Default field to sort by
 * @param {string} options.defaultSortDirection - Default sort direction ('asc' or 'desc')
 * @param {Object} options.sortFieldConfig - Configuration for sorting fields
 * @param {Object} options.defaultSorts - Default sort directions for each field
 * @returns {Object} - Pagination and sorting state and handlers
 */
export const useTablePagination = (items = [], options = {}) => {
    const {
        itemsPerPage = 10,
        defaultSortField,
        defaultSortDirection = 'desc',
        sortFieldConfig = {},
        defaultSorts = {}
    } = options;

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);

    // Sorting state
    const [sortField, setSortField] = useState(defaultSortField);
    const [sortDirection, setSortDirection] = useState(defaultSortDirection);

    // Create sort function based on current sort field and direction
    const sortFunction = useMemo(() => {
        if (!sortField || !sortFieldConfig[sortField]) {
            return null;
        }
        return createSortFunction(sortField, sortDirection, sortFieldConfig);
    }, [sortField, sortDirection, sortFieldConfig]);

    // Sort items
    const sortedItems = useMemo(() => {
        if (!items || items.length === 0) return [];
        if (!sortFunction) return items;

        return [...items].sort(sortFunction);
    }, [items, sortFunction]);

    // Calculate pagination
    const pagination = useMemo(() => {
        return calculatePagination(sortedItems, currentPage, itemsPerPage);
    }, [sortedItems, currentPage, itemsPerPage]);

    // Create sort handler
    const handleSort = useCallback(
        createSortHandler(
            sortField,
            sortDirection,
            setSortField,
            setSortDirection,
            setCurrentPage,
            defaultSorts
        ),
        [sortField, sortDirection, defaultSorts]
    );

    // Page change handler
    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
    }, []);

    // Reset to first page when items change
    const resetToFirstPage = useCallback(() => {
        setCurrentPage(1);
    }, []);

    return {
        // Pagination state
        currentPage,
        itemsPerPage,
        totalPages: pagination.totalPages,
        startIndex: pagination.startIndex,
        endIndex: pagination.endIndex,
        currentItems: pagination.currentItems,

        // Sorting state
        sortField,
        sortDirection,
        sortedItems,

        // Handlers
        handlePageChange,
        handleSort,
        resetToFirstPage,

        // Setters (for external control)
        setCurrentPage,
        setSortField,
        setSortDirection
    };
};
